import { useCallback, useMemo, useRef, useState } from "react";
import { VivoPlotWrapper } from "./wrapper";
import { Button, Modal, Segmented } from "antd";
import { ModalTab } from "./components/ModalTab";
import { PlotTypeButtons } from "./components/PlotTypeButtons";
import PlotDataTable from "./components/PlotDataTable";
import ClientDownloadButton from "@/components/UI/ClientDownloadButton/ClientDownloadButton";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";
import DownloadIcon from "@/assets/icons/download.svg?react";
import PlotIcon from "@/assets/icons/plots_menu.svg?react";
import TableIcon from "@/assets/icons/table.svg?react";

import { usePlotExport } from "./usePlotExport";
import { usePlotSetup } from "./usePlotSetup";
import type {
  ColumnOption,
  PlotMeta,
  PlotTableView,
  PreviewMode,
} from "@/components/VivoPlots/types";
import { columnTitle } from "@/components/VivoPlots/functions/plotTableView";

export default function VivoPlotModal({
  datasetName,
  initialPlotSetup = null,
  isOpen,
  onCancel,
  onConfirm,
  columnOptions,
  globalFilters,
  endpointSuffix,
  endpointPrefix,
  columnsTitleCreator,
  metadata,
  subjectId,
}) {
  return (
    <Modal
      destroyOnHidden={true}
      open={isOpen}
      onCancel={onCancel}
      centered
      width={"85%"}
      bodyProps={{
        className:
          // Give the modal body a fixed viewport height and hide outer overflow.
          "bg-[#D2D4D5] p-0 h-[90vh] rounded-lg overflow-hidden",
      }}
      footer={null}
      className="no-padding-modal"
    >
      <ModalContent
        initialPlotSetup={initialPlotSetup}
        datasetName={datasetName}
        onCancel={onCancel}
        onConfirm={onConfirm}
        columnsTitleCreator={columnsTitleCreator}
        columnOptions={columnOptions}
        globalFilters={globalFilters}
        endpointSuffix={endpointSuffix}
        endpointPrefix={endpointPrefix}
        metadata={metadata}
        subjectId={subjectId}
      />
    </Modal>
  );
}

const SizeWrapper = ({ children, size, className }) => {
  if (size === "small") {
    return (
      <div className={`grid grid-cols-4 gap-0 h-full w-full ${className}`}>
        <div className="col-start-2 col-span-2 h-full">{children}</div>
      </div>
    );
  }
  if (size === "large") {
    return (
      <div className={`grid grid-cols-3 gap-0 h-full w-full ${className}`}>
        <div className="col-span-3 h-full">{children}</div>
      </div>
    );
  }
  return (
    <div className={`grid grid-cols-6 gap-0 h-full w-full ${className}`}>
      <div className="col-start-2 col-span-4 h-full">{children}</div>
    </div>
  );
};

const ModalContent = ({
  initialPlotSetup,
  onCancel,
  onConfirm,
  columnOptions,
  columnsTitleCreator,
  globalFilters,
  endpointSuffix,
  endpointPrefix,
  datasetName,
  metadata,
  subjectId,
}) => {
  const typedColumnOptions: ColumnOption[] = useMemo(() => {
    if (!columnOptions) {
      return [];
    }
    return columnOptions.map((column) => ({
      name: column,
      data_type: metadata?.filters?.[column]?.data_type ?? null,
    }));
  }, [columnOptions, metadata]);

  const { plot, setters, getters } = usePlotSetup({
    initialPlotSetup,
    columnsTitleCreator,
    columnOptions: typedColumnOptions,
    globalFilters,
  });

  const [previewMode, setPreviewMode] = useState<PreviewMode>("plot");
  const [plotTable, setPlotTable] = useState<PlotTableView>({
    columns: [],
    rows: [],
  });
  const previousTableSignatureRef = useRef("");
  const previewRootRef = useRef<HTMLDivElement>(null);
  const exportPlotPng = usePlotExport();
  // Metric cards have no axes — completeness is defined by the metric config.
  const metricCardConfigComplete =
    getters.plotType !== "metric-card" ||
    (Boolean(getters.metricAggregation) &&
      (["count-distinct", "sum", "mean", "min", "max"].includes(
        getters.metricAggregation,
      )
        ? Boolean(getters.metricValueColumn)
        : true) &&
      !(
        getters.metricDisplayStyle === "donut" &&
        getters.metricDenominatorSource === "fixed" &&
        !getters.metricDenominatorFixed
      ));
  const canCreatePlot =
    Boolean(getters.plotTitle) &&
    !getters.hasFilterConflicts &&
    (getters.plotType === "metric-card"
      ? metricCardConfigComplete
      : Boolean(getters.xVariable) &&
        (getters.plotType === "barplot" || Boolean(getters.yVariable)));
  // PNG export serializes the d3 SVG, which metric cards and line charts don't render.
  const canExportPng =
    canCreatePlot &&
    getters.plotType !== "metric-card" &&
    getters.plotType !== "line";
  const exportButtonTitle = canExportPng
    ? "Export plot as PNG"
    : getters.plotType === "metric-card" || getters.plotType === "line"
      ? "PNG export is not available for this plot type"
      : getters.plotType === "barplot"
        ? "Select the required X axis to enable PNG export"
        : "Select the required X and Y axes to enable PNG export";

  const handlePlotMeta = useCallback(({ tableView }: PlotMeta) => {
    const nextTableSignature = JSON.stringify(tableView);

    if (nextTableSignature !== previousTableSignatureRef.current) {
      previousTableSignatureRef.current = nextTableSignature;
      setPlotTable(tableView);
    }
  }, []);

  const exportData = useMemo(
    () => ({
      fields: plotTable.columns.map((column) =>
        columnTitle(column, columnsTitleCreator),
      ),
      rows: plotTable.rows.map((row) =>
        plotTable.columns.map((column) => row[column.key] ?? ""),
      ),
    }),
    [columnsTitleCreator, plotTable],
  );

  const normalizedPlotTitle =
    (getters.plotTitle || "plot")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w-]/g, "") || "plot";

  return (
    <div className="w-full h-full flex flex-row items-stretch relative">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <div className="bg-[#D2D4D5] flex items-center justify-center py-0 px-1 rounded-xs">
          <p className="text-[#2B4752] text-[14px] font-semibold">PREVIEW</p>
        </div>
        <Segmented<PreviewMode>
          value={previewMode}
          onChange={setPreviewMode}
          aria-label="Preview mode"
          options={[
            {
              value: "plot",
              label: (
                <span
                  aria-label="Plot view"
                  className="inline-flex items-center gap-1"
                >
                  <PlotIcon aria-hidden="true" className="h-4 w-4" />
                  <span>Plot</span>
                </span>
              ),
            },
            {
              value: "data",
              label: (
                <span
                  aria-label="Data view"
                  className="inline-flex items-center gap-1"
                >
                  <TableIcon aria-hidden="true" className="h-4 w-4" />
                  <span>Data</span>
                </span>
              ),
            },
          ]}
        />
      </div>
      {/* Left preview area */}
      <div
        ref={previewRootRef}
        className="relative px-6 flex items-center justify-center w-full p-[60px]"
      >
        <SizeWrapper
          size={plot.config.plotSize}
          className={`content-center ${
            previewMode === "data" ? "invisible" : ""
          }`}
        >
          <VivoPlotWrapper
            datasetName={datasetName}
            inModal={true}
            globalFilters={globalFilters}
            plotFilters={plot.plotFilters}
            endpointSuffix={endpointSuffix}
            endpointPrefix={endpointPrefix}
            metadata={metadata}
            columnsTitleCreator={columnsTitleCreator}
            subjectId={subjectId}
            {...plot}
            onPlotMetadataChange={handlePlotMeta}
          />
        </SizeWrapper>
        {previewMode === "data" && (
          <>
            <div className="absolute inset-0 p-[60px] flex items-center">
              <PlotDataTable
                columns={plotTable.columns}
                rows={plotTable.rows}
                columnsTitleCreator={columnsTitleCreator}
              />
            </div>
            <div className="absolute top-4 right-4 z-20">
              <ClientDownloadButton
                fields={exportData.fields}
                rows={exportData.rows}
                fileName={`${normalizedPlotTitle}_data`}
              />
            </div>
          </>
        )}
        {previewMode === "plot" && (
          <div className="absolute top-4 right-4 z-20">
            <IconButton
              data-testid="preview-export-png"
              disabled={!canExportPng}
              icon={
                <>
                  <DownloadIcon aria-hidden="true" />
                  <span className="sr-only">Export plot as PNG</span>
                </>
              }
              title={exportButtonTitle}
              onClick={() => {
                if (!previewRootRef.current || !canExportPng) return;
                exportPlotPng(
                  "vivo-plot-modal",
                  getters.plotTitle,
                  previewRootRef.current,
                );
              }}
            />
          </div>
        )}
      </div>
      {/* Right panel: make it full height, column, and contain its own overflow */}
      <div className="w-[500px] h-full flex flex-col bg-white overflow-hidden">
        {/* Header (fixed height, non-scrolling) */}
        <div className="p-4 pr-0 border-b border-[#F0F0F0]">
          <h2 className="text-base text-[#2B4752] font-semibold">
            Create a Plot
          </h2>
        </div>

        {/* Controls above the scroller, if desired keep here or move inside scroller */}
        <div className="px-4 pt-3 ">
          <PlotTypeButtons
            plotType={getters.plotType}
            setPlotType={setters.setPlotType}
          />
        </div>

        {/* SCROLL AREA: flex-1 so it takes remaining space; min-h-0 enables flex children to shrink; overflow for scroll */}
        <div className="flex-1 min-h-0 pl-4">
          <div className="h-full min-h-0 flex mb-4">
            <ModalTab
              columnsTitleCreator={columnsTitleCreator}
              setters={setters}
              getters={getters}
              columnOptions={typedColumnOptions}
              metadata={metadata}
              globalFilters={globalFilters}
              defaultActiveKey={
                initialPlotSetup?.openToDataTab ? "data" : "config"
              }
            />
          </div>
        </div>

        {/* Footer: always visible since it's outside the scroll area */}
        <div className="flex justify-between gap-2 p-4 border-t border-[#F0F0F0]">
          <Button
            type="text"
            className="text-sm text-[#064C68] font-semibold border-0 p-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            className="bg-[#0C98D0] text-white border-[#0C98D0]"
            disabled={!canCreatePlot}
            onClick={() => {
              onConfirm(plot);
              onCancel();
            }}
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};
